import { useState, useEffect, useCallback } from "react";
import { useSubsheets } from "./useSubsheets";

import { ALL_ITEMS_ID } from "@/lib/constants";

const STORAGE_KEY = "selected-subsheet-id";
const CHANGE_EVENT = "subsheet-changed";

export const useSelectedSubsheet = () => {
  const { data: subsheets = [], isLoading } = useSubsheets();

  const [selectedSubsheetId, setSelectedSubsheetIdState] = useState<string | null>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored || null;
  });

  // Once subsheets are loaded, set default if nothing is selected. The backlog
  // opens on "All items" — departments are a narrowing step, not the entry
  // point (UX pass: All items sits first in the sidebar and is the default).
  useEffect(() => {
    if (!isLoading && subsheets.length > 0 && !selectedSubsheetId) {
      setSelectedSubsheetIdState(ALL_ITEMS_ID);
      sessionStorage.setItem(STORAGE_KEY, ALL_ITEMS_ID);
    }
  }, [isLoading, subsheets, selectedSubsheetId]);

  // Validate stored ID still exists in subsheets; a stale selection falls back
  // to the same "All items" default rather than an arbitrary department.
  useEffect(() => {
    if (!isLoading && subsheets.length > 0 && selectedSubsheetId) {
      if (selectedSubsheetId === ALL_ITEMS_ID) return;
      const exists = subsheets.some((s) => s.id === selectedSubsheetId);
      if (!exists) {
        setSelectedSubsheetIdState(ALL_ITEMS_ID);
        sessionStorage.setItem(STORAGE_KEY, ALL_ITEMS_ID);
      }
    }
  }, [isLoading, subsheets, selectedSubsheetId]);

  // Listen for changes from other components
  useEffect(() => {
    const handler = () => {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && stored !== selectedSubsheetId) {
        setSelectedSubsheetIdState(stored);
      }
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, [selectedSubsheetId]);

  const setSelectedSubsheetId = useCallback((subsheetId: string | null) => {
    if (subsheetId) {
      setSelectedSubsheetIdState(subsheetId);
      sessionStorage.setItem(STORAGE_KEY, subsheetId);
      // Notify other hook instances
      window.dispatchEvent(new Event(CHANGE_EVENT));
    }
  }, []);

  return {
    selectedSubsheetId,
    setSelectedSubsheetId,
    isLoading,
  };
};
